import { ResourceTileClient } from "../clients/ResourceTileClient";

export interface RichTextDescription {
  content?: {
    content?: {
      value?: string;
    }[];
  }[];
}

interface ContentfulAsset {
  sys?: { id?: string };
  fields?: {
    title?: string;
    description?: string;
    file?: {
      url?: string;
    };
  };
}

interface ContentfulSortingData {
  priority?: number;
  [key: string]: unknown;
}

interface ContentfulTileData {
  keyword?: string[];
  sorting?: ContentfulSortingData;
  [key: string]: unknown;
}

export interface ResourceTileProps {
  id: string;
  title: string;
  description: string | RichTextDescription;
  thumbnail: string;
  displayName: string;
  url: string;
  tags: string[];
  data: ContentfulTileData;
}

export interface ContentPageSlideProps {
  id: string;
  title: string;
  description: string | RichTextDescription;
  images: ContentfulAsset[];
  tags: string[];
  sequence: string;
  displayName: string;
}

interface RawThumbnail {
  fields?: {
    file?: {
      url?: string;
    };
  };
}

interface RawResourceTile {
  sys?: { id?: string };
  fields?: {
    title?: string;
    description?: ResourceTileProps["description"];
    thumbnail?: RawThumbnail;
    displayName?: string;
    url?: string;
    tags?: string[];
    data?: ContentfulTileData;
  };
}

interface RawContentPageEntry {
  sys?: { id?: string };
  fields?: {
    title?: string;
    description?: ContentPageSlideProps["description"];
    images?: ContentPageSlideProps["images"];
    tags?: string[];
    displayName?: string;
    data?: {
      sequence?: string;
    };
  };
}

export const cleanUpResourceTiles = (
  rawData: unknown[],
): ResourceTileProps[] => {
  return (rawData as RawResourceTile[]).map((tile) => ({
    id: tile.sys?.id ?? "",
    title: tile.fields?.title ?? "No title",
    description: tile.fields?.description ?? "No description",
    thumbnail: tile.fields?.thumbnail?.fields?.file?.url ?? "",
    displayName: tile.fields?.displayName ?? "",
    url: tile.fields?.url ?? "#",
    tags: tile.fields?.tags ?? [],
    data: tile.fields?.data ?? {},
  }));
};

export const cleanUpContentPageSlides = (
  rawData: unknown[],
): ContentPageSlideProps[] => {
  return (rawData as RawContentPageEntry[]).map((entry) => ({
    id: entry.sys?.id ?? "",
    title: entry.fields?.title ?? "No title",
    description: entry.fields?.description ?? "No description",
    images: entry.fields?.images ?? [],
    tags: entry.fields?.tags ?? [],
    sequence: entry.fields?.data?.sequence ?? "",
    displayName: entry.fields?.displayName ?? "",
  }));
};

export interface TaggedResourceTiles {
  data: ResourceTileProps[];
  tagFilter: string;
}

export const fetchResourceTilesByTagFilters = async (
  tagFilters: string[],
): Promise<TaggedResourceTiles[]> => {
  const requests = tagFilters.map(async (tagFilter) => {
    const resources = [];
    let total = Infinity;
    let offset = 0;

    while (offset < total) {
      const data = await ResourceTileClient.getEntries({
        skip: offset,
        limit: 1000,
        content_type: "resourceTile",
        "fields.tags[in]": tagFilter,
      });

      if (data.items.length === 0) break;

      total = data.total;
      offset += data.items.length;
      resources.push(...data.items);
    }

    return {
      data: cleanUpResourceTiles(resources),
      tagFilter: tagFilter,
    };
  });

  return await Promise.all(requests);
};

export const fetchResourceTilesByIds = async (
  resourceIds: string[],
): Promise<ResourceTileProps[]> => {
  if (resourceIds.length === 0) return [];

  const data = await ResourceTileClient.getEntries({
    content_type: "resourceTile",
    "sys.id[in]": resourceIds,
  });

  return cleanUpResourceTiles(data.items);
};

export function descriptionToString(
  description: RichTextDescription | string,
): string {
  if (typeof description === "string") {
    return description;
  } else {
    return (description.content ?? [])
      .flatMap((block) => block.content ?? [])
      .map((textNode) => textNode.value ?? "")
      .join("");
  }
}
